/* eslint-disable ember/no-computed-properties-in-native-classes */
/* eslint-disable ember/no-runloop */
import { computed, set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import Component from '@glimmer/component';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import type * as Highcharts from 'highcharts';
import type { RevenueInsightSegmentationValue } from 'lumen/models';
import { timeout } from 'lumen/utils/waiters';

interface ExtendedAxis extends Highcharts.Axis {
  dataMax: number;
  dataMin: number;
}

export interface HotelAndMarketRevenueInsightBasePaceBaseSignature {
  Args: {
    chartSelectionChanged: (
      id: string,
      range: { end: Dayjs; start: Dayjs },
    ) => void;
    data?: RevenueInsightSegmentationValue[];
    filterLimits: { pace: number };
  };
}

export default abstract class HotelAndMarketRevenueInsightBasePaceBase<
  T extends HotelAndMarketRevenueInsightBasePaceBaseSignature =
    HotelAndMarketRevenueInsightBasePaceBaseSignature,
> extends Component<T> {
  tooltipData: RevenueInsightSegmentationValue | null = null;

  yAxis: Highcharts.YAxisOptions = {
    plotLines: [
      {
        value: 0,
        width: 1,
      },
    ],
    title: {
      text: null,
    },
    tickPositioner() {
      const { dataMax } = this as ExtendedAxis;

      const positions = [];
      // Dynamic rounding
      // Between 0 and 1000 > round to 10
      // Between 1000 and 10.000 round to 100
      // Above 10.000 round to nearest 1000
      let rounding = 10;
      if (dataMax > 10000) {
        rounding = 1000;
      } else if (dataMax > 1000) {
        rounding = 100;
      }
      let increment = Math.ceil(dataMax / (4 * rounding)) * rounding;
      // Have a minimum increment of 1 to prevent infinite looping when dataMax is 0.
      increment = Math.max(increment, 1);
      // Start at 0, go until you pass the max
      if (dataMax !== null) {
        for (let tick = 0; tick - increment <= dataMax; tick += increment) {
          positions.push(tick);
        }
      }
      return positions;
    },
  };

  zoomTask = task({ drop: true }, async (range): Promise<void> => {
    // Since the drag action could fire twice (highcharts functionality/bug) we added a debounce of 250ms to only perform the selection once
    await timeout();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.args.chartSelectionChanged(RI_BREAKDOWNS.PACE, range);
  });

  @computed('args.filterLimits.pace', 'tooltip')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseChartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const { yAxis } = this;
    const {
      args: { filterLimits },
      zoomTask,
    } = this;
    // Can only drag when you have filtercapacity left
    const canDrag = filterLimits.pace > 0;

    return {
      chart: {
        height: '204px',
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
        resetZoomButton: {
          theme: {
            display: 'none',
          },
        },
        // Note: allowing the range selection by dragging the mouse
        zoomType: canDrag ? ('x' as const) : undefined,
        events: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          click(e: Highcharts.PointerEventObject) {
            const typedEvent = e as Highcharts.ChartClickEventObject;

            if (canDrag && typedEvent.xAxis[0]) {
              that.handleClick(typedEvent.xAxis[0].value);
            }
          },
        },
      },
      title: {
        text: 'chart-pace',
      },
      xAxis: {
        allowDecimals: false,
        type: 'datetime' as const,
        endOnTick: false,
        labels: {
          rotation: -45,
        },
        ordinal: false,
        minTickInterval: 24 * 60 * 60 * 1000,
        events: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          afterSetExtremes(e: Highcharts.AxisSetExtremesEventObject) {
            // We need to use afterSetExtremes instead of set extremes
            // setExtremes executes the moment you let go of the zoom
            // afterSetExtremes only runs after the chart has redrawn
            // This behaviour is better, since we can get unexpected errors if we change chart options during a redraw
            // This does mean we need to check the trigger of the event, since this is not only after a zoom
            if (e.trigger && e.trigger === 'zoom') {
              if (canDrag) {
                // Use userMax and userMin. The max and min properties take the minInterval into account, we don't want that
                // Add 12 hours to prevent wrong conversion from x value (continuous var) to date
                const range = {
                  start: dayjs.utc(e.userMin).add(12, 'hour'),
                  end: dayjs.utc(e.userMax).add(12, 'hour'),
                };
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                zoomTask.perform(range);
              }
            }
          },
        },
      },
      yAxis,
      plotOptions: {
        area: {
          stacking: 'normal' as const,
        },
        series: {
          // Remove when API doesn't return all data
          turboThreshold: 0,
          cursor: canDrag ? 'pointer' : 'default',
          point: {
            events: {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              click(e: Highcharts.PointClickEventObject) {
                if (canDrag) {
                  that.handleClick(e.point.x);
                }
              },
            },
          },
        },
      },
      tooltip: this.tooltip,
    };
  }

  @computed('args.data.[]')
  get sortedData(): RevenueInsightSegmentationValue[] {
    if (!this.args.data?.length) {
      return [];
    }

    return this.args.data.toSorted((a, b) => {
      const dateA = new Date(a.segmentationValue).getTime();
      const dateB = new Date(b.segmentationValue).getTime();

      return dateA - dateB;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltip() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      shared: true,
      crosshairs: true,
      padding: 0,
      borderWidth: 0,
      shadow: false,
      outside: true,
      positioner: undefined,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const dateValue = dayjs.utc(this.x, 'x').format('YYYY-MM-DD');
        const stayDate =
          that.args.data?.find(
            (date) => date.segmentationValue === dateValue,
          ) ?? null;
        begin();
        set(that, 'tooltipData', stayDate);
        end();
        return document.getElementById('dummy-div-for-tooltip-pace-chart')
          ?.innerHTML;
      },
      useHTML: true,
    };
  }

  handleClick(x: number): void {
    // Add 12 hours to prevent wrong conversion from x value (continuous var) to date
    const dateClicked = dayjs.utc(x).add(12, 'hour');
    this.args.chartSelectionChanged(RI_BREAKDOWNS.PACE, {
      start: dateClicked.subtract(1, 'day'),
      end: dateClicked,
    });
  }
}
